import {FC, useCallback, useEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {
    Checkmark16Regular,
    ChevronDown16Regular,
    Dismiss16Regular,
    Flash16Regular,
    PersonAdd24Regular,
    Search16Regular
} from "@fluentui/react-icons";
import {Assignee, currentUser, teammates} from "../assignableTeam";

interface IAssignControlProps {
    assignee: Assignee | null;
    onAssign: (person: Assignee) => void;
    onUnassign: () => void;
}

const AssignControl: FC<IAssignControlProps> = ({
    assignee,
    onAssign,
    onUnassign
}) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const triggerRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const [menuPos, setMenuPos] = useState<{
        left: number;
        top?: number;
        bottom?: number;
    }>({left: 0});

    const updatePosition = useCallback(() => {
        const el = triggerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const MENU_WIDTH = 280;
        const GAP = 8;
        const ESTIMATED_HEIGHT = 380;
        const left = Math.max(
            8,
            Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8)
        );
        const spaceBelow = window.innerHeight - rect.bottom;
        const dropUp = spaceBelow < ESTIMATED_HEIGHT && rect.top > spaceBelow;
        if (dropUp) {
            setMenuPos({left, bottom: window.innerHeight - rect.top + GAP});
        } else {
            setMenuPos({left, top: rect.bottom + GAP});
        }
    }, []);

    useEffect(() => {
        if (!open) return;
        updatePosition();
        const handleClick = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                triggerRef.current &&
                !triggerRef.current.contains(target) &&
                menuRef.current &&
                !menuRef.current.contains(target)
            ) {
                setOpen(false);
            }
        };
        const handleReposition = () => updatePosition();
        document.addEventListener("mousedown", handleClick);
        window.addEventListener("scroll", handleReposition, true);
        window.addEventListener("resize", handleReposition);
        return () => {
            document.removeEventListener("mousedown", handleClick);
            window.removeEventListener("scroll", handleReposition, true);
            window.removeEventListener("resize", handleReposition);
        };
    }, [open, updatePosition]);

    const filteredTeammates = teammates.filter((person) => {
        const query = search.trim().toLowerCase();
        if (!query) return true;
        return (
            person.name.toLowerCase().includes(query) ||
            person.role.toLowerCase().includes(query)
        );
    });

    const select = (person: Assignee) => {
        onAssign(person);
        setOpen(false);
        setSearch("");
    };

    return (
        <div className="par-assign" ref={triggerRef}>
            {assignee ? (
                <button
                    type="button"
                    className={`par-assign__pill${
                        assignee.isSelf ? " par-assign__pill--self" : ""
                    }`}
                    onClick={() => setOpen((value) => !value)}
                >
                    <span className="par-assign-avatar">{assignee.initials}</span>
                    <span className="par-assign__pill-text">
                        <span className="par-assign__pill-name">
                            {assignee.isSelf ? "You" : assignee.name}
                        </span>
                        <span className="par-assign__pill-label">Assigned</span>
                    </span>
                    <ChevronDown16Regular className="par-assign__caret" />
                </button>
            ) : (
                <button
                    type="button"
                    className="par-assign__trigger"
                    onClick={() => setOpen((value) => !value)}
                >
                    <PersonAdd24Regular className="par-assign__trigger-icon" />
                    Assign
                </button>
            )}

            {open &&
                createPortal(
                    <div
                        ref={menuRef}
                        className="par-assign__menu par-assign__menu--floating"
                        role="menu"
                        style={{
                            left: menuPos.left,
                            top: menuPos.top,
                            bottom: menuPos.bottom
                        }}
                    >
                        <button
                            type="button"
                            className={`par-assign__option par-assign__option--self${
                                assignee?.isSelf ? " par-assign__option--active" : ""
                            }`}
                            onClick={() => select(currentUser)}
                        >
                            <span className="par-assign-avatar par-assign-avatar--self">
                                {currentUser.initials}
                            </span>
                            <span className="par-assign__option-body">
                                <span className="par-assign__option-name">
                                    Assign to me
                                </span>
                                <span className="par-assign__option-role">
                                    Adds to your Follow Up Tasks
                                </span>
                            </span>
                            {assignee?.isSelf ? (
                                <Checkmark16Regular className="par-assign__option-icon" />
                            ) : (
                                <Flash16Regular className="par-assign__option-icon" />
                            )}
                        </button>

                        <div className="par-assign__section-label">
                            Assign to a teammate
                        </div>

                        <div className="par-assign__search">
                            <Search16Regular className="par-assign__search-icon" />
                            <input
                                className="par-assign__search-input"
                                placeholder="Search name or role…"
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                            />
                        </div>

                        <div className="par-assign__list">
                            {filteredTeammates.length === 0 ? (
                                <div className="par-assign__empty">No matches</div>
                            ) : (
                                filteredTeammates.map((person) => {
                                    const isActive = assignee?.id === person.id;
                                    return (
                                        <button
                                            key={person.id}
                                            type="button"
                                            className={`par-assign__option${
                                                isActive
                                                    ? " par-assign__option--active"
                                                    : ""
                                            }`}
                                            onClick={() => select(person)}
                                        >
                                            <span className="par-assign-avatar">
                                                {person.initials}
                                            </span>
                                            <span className="par-assign__option-body">
                                                <span className="par-assign__option-name">
                                                    {person.name}
                                                </span>
                                                <span className="par-assign__option-role">
                                                    {person.role}
                                                </span>
                                            </span>
                                            {isActive && (
                                                <Checkmark16Regular className="par-assign__option-icon" />
                                            )}
                                        </button>
                                    );
                                })
                            )}
                        </div>

                        {assignee && (
                            <button
                                type="button"
                                className="par-assign__unassign"
                                onClick={() => {
                                    onUnassign();
                                    setOpen(false);
                                }}
                            >
                                <Dismiss16Regular className="par-assign__unassign-icon" />
                                Unassign
                            </button>
                        )}
                    </div>,
                    document.body
                )}
        </div>
    );
};

export default AssignControl;
